// Resolve the API base URL at runtime, NOT just build time, so the same
// build works locally, via ngrok / cloudflared tunnels, and on Render:
//
//   - Local dev (page at localhost:5173): use VITE_API_URL when set (so
//     direct API calls hit the backend on :4000 without bouncing through
//     the Vite proxy), else fall back to a relative path.
//   - Tunnels (ngrok / cloudflared / loca.lt etc.): IGNORE the baked-in
//     localhost URL — the remote browser can't reach the dev's localhost.
//     Use a relative path so requests go to the SAME tunnel host and
//     Vite's `/api -> localhost:4000` proxy handles them server-side.
//   - Render: use the explicit prod backend URL.
//
// This is the difference between "works on my machine" and "works
// everywhere the frontend is served from".
function resolveApiBase(): string {
  const baked = (import.meta.env.VITE_API_URL || '').trim();
  if (typeof window === 'undefined') return baked; // SSR / build-time
  const host = window.location.hostname;
  if (host.endsWith('.onrender.com')) {
    return 'https://spares-backend-f8mg.onrender.com';
  }
  // Recognise tunnel hosts — the baked localhost URL is wrong for them.
  const isTunnel = /\.(ngrok-free\.dev|ngrok-free\.app|ngrok\.io|ngrok\.app|trycloudflare\.com|loca\.lt)$/.test(host);
  if (isTunnel) return ''; // relative — same-origin, Vite proxy handles `/api`
  // Local dev and everything else — prefer baked URL, fall back to relative.
  return baked;
}
const API_URL = resolveApiBase();
// Re-exported so other modules (main.tsx keep-warm ping, etc.) use the SAME
// host-aware resolution instead of reading the baked env var directly and
// hitting a tunnel-incompatible localhost URL.
export function getApiBase(): string { return API_URL; }

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

/**
 * Auth strategy: backend returns a JWT both in a cookie AND in the response
 * body. iOS Safari's ITP blocks cross-origin cookies, so on top of the cookie
 * we also persist the token in localStorage and send it as a Bearer header.
 * Desktop/Android keep working via the cookie; iOS works via the header.
 */
const TOKEN_KEY = 'ims_token';

export function setToken(t: string | null) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* Safari private mode etc. — ignore */ }
}

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

/**
 * Soft timeout for every API call. A hung server (process crashed mid-request,
 * laptop went to sleep, ngrok tunnel died) used to leave mutations stuck in
 * `isPending: true` forever — visible to the user as a button perpetually
 * stuck on "Saving…". Aborting after 30s lets React Query fire `onError` so
 * the UI recovers gracefully.
 */
const REQUEST_TIMEOUT_MS = 30_000;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort('timeout'), REQUEST_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      credentials: 'include',
      signal: ac.signal,
      // `no-cache` forces the browser to revalidate with the server on every
      // request instead of silently serving a 304 with the previously-cached
      // body. We saw this break the PO detail modal: after adding a remark,
      // React Query refetched, but the browser said "your cache is fine →
      // 304" and reused the OLD body without the new remark. Even safe GETs
      // on this app are user-specific + change frequently, so HTTP caching
      // does more harm than good. Has no measurable perf impact since the
      // server response is already small + gzip'd.
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers || {}),
      },
      ...init,
    });
  } catch (e: any) {
    clearTimeout(timer);
    if (e?.name === 'AbortError') {
      throw new ApiError(0, 'Request timed out — check your connection or server status.');
    }
    throw new ApiError(0, e?.message || 'Network error');
  }
  clearTimeout(timer);
  const ct = res.headers.get('content-type') || '';
  const data = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (data as any)?.error || res.statusText;
    // Clear a broken token on 401 AND broadcast an event so the auth context
    // can reset its React `user` state and bounce to /login. Without the
    // broadcast the localStorage clear was a half-measure: the protected
    // routes kept rendering with stale user state and every subsequent call
    // 401'd silently in the background.
    if (res.status === 401 && token) {
      setToken(null);
      try {
        window.dispatchEvent(new CustomEvent('auth:expired'));
      } catch { /* SSR / non-DOM env — ignore */ }
    }
    throw new ApiError(res.status, msg, (data as any)?.details ?? (data as any)?.issues);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: async <T>(path: string, file: File, extra: Record<string, string> = {}): Promise<T> => {
    const fd = new FormData();
    fd.append('file', file);
    for (const [k, v] of Object.entries(extra)) fd.append(k, v);
    const token = getToken();
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new ApiError(res.status, data?.error || 'Upload failed');
    return data as T;
  },
  // Multipart POST with a caller-built FormData (multiple files + fields).
  uploadForm: async <T>(path: string, fd: FormData): Promise<T> => {
    const token = getToken();
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new ApiError(res.status, data?.error || 'Upload failed');
    return data as T;
  },
  // Multipart PATCH with a caller-built FormData — same shape as uploadForm
  // but for edit-in-place routes (e.g. replacing a quotation's file while
  // also updating its text fields in one request).
  patchForm: async <T>(path: string, fd: FormData): Promise<T> => {
    const token = getToken();
    const res = await fetch(`${API_URL}${path}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new ApiError(res.status, data?.error || 'Update failed');
    return data as T;
  },
};

/**
 * Build a URL for a GridFS file that an <img>/<a> tag can actually load.
 * The /api/files/:id endpoint is auth-gated; <img> tags can't send a Bearer
 * header and the JWT cookie is cross-site in production, so we append the
 * token as a query param (the backend copies it into the Authorization
 * header before auth runs). Returns '' for a missing id so callers can
 * guard on falsy.
 */
export function fileUrl(id?: string | null): string {
  if (!id) return '';
  const token = getToken();
  return `${API_URL}/api/files/${id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
}

/**
 * Open a stored file in a new tab — robustly. A plain `<a href={fileUrl(id)}>`
 * relies on (a) the `?token=` query auth and (b) the host's /api proxy forwarding
 * the file response intact; on some deployments (e.g. the separate procurement
 * portal behind nginx) that navigation gets swallowed and the tab shows the SPA
 * or a blank page. This instead fetches the bytes with the SAME Bearer-header
 * auth that every data call uses (which is known-good on those hosts), then opens
 * the result as a same-origin blob — so it works regardless of proxy quirks.
 *
 * Claims the new tab synchronously during the click gesture so the pop-up
 * blocker doesn't kill it after the await. Throws on failure so callers can toast.
 */
export async function openFileInNewTab(id?: string | null): Promise<void> {
  if (!id) return;
  const w = window.open('', '_blank'); // reserve the tab inside the user gesture
  try {
    const token = getToken();
    const res = await fetch(`${API_URL}/api/files/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) throw new ApiError(res.status, `Could not open file (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (w && !w.closed) w.location.href = url;
    else window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    if (w && !w.closed) w.close();
    throw e;
  }
}

/**
 * Download an authenticated endpoint's response as a file.
 *
 * Same reasoning as `openFileInNewTab`: a plain link can't carry the Bearer
 * header, and the `?token=` fallback doesn't survive every proxy. Fetches the
 * bytes, then hands the browser a same-origin blob to save.
 *
 * Returns the response headers so a caller can report what the server said —
 * the trip document pack, for instance, reports how many files it couldn't
 * embed, and silently dropping that would make an incomplete pack look whole.
 */
export async function downloadFile(
  path: string,
  fallbackName = 'download',
): Promise<Headers> {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try { msg = (await res.json())?.error || msg; } catch { /* not json */ }
    throw new ApiError(res.status, msg);
  }
  // Prefer the server's filename; it names the trip and technician.
  const disp = res.headers.get('content-disposition') || '';
  const named = /filename="?([^";]+)"?/i.exec(disp)?.[1];
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = named || fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return res.headers;
}

export { API_URL };
